import React, { useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Popconfirm, Tooltip, Typography } from 'antd';

import {
    updateAccountTab,
    updateScrollSection,
} from '../../../../../../account/store/accountSettings.actions';
import { setUserData } from '../../../../../../../../userBrowserSettings/store/browserSettings.actions';
import {
    selectCurrentUserRoles,
    selectDarkMode,
    selectUserData,
} from '../../../../../../../../userBrowserSettings/store/browserSettings.selectors';

import { accountTabs } from '../../../../../../../../constants/accountTabs';
import { FILTERED_TERMS } from '../../../../../../../../constants/constants';
import { notificationType } from '../../../../../../../../constants/notificationType';
import { organizationPathes } from '../../../../../../../../constants/queryPathes';
import { roleTypes } from '../../../../../../../../constants/roleTypes';
import { ScrollIds } from '../../../../../../../../constants/scrollIds';

import { axiosAPI } from '../../../../../../../../utils/axiosAPI';
import { getAxiosHeaders } from '../../../../../../../../utils/helpers/getAxiosHeaders';
import { isCustomKeywordSource } from '../../../../../../../../utils/helpers/getSortedOptimizerKeywords';
import { isKeywordBlocked } from '../../../../../../../../utils/helpers/keywordHelpers';
import { openBasicErrorNotification } from '../../../../../../../../utils/helpers/openBasicErrorNotification';
import { openNotification } from '../../../../../../../../utils/helpers/openNotification';
import {
    getThemeColorClass,
    getTooltipOverlayClass,
} from '../../../../../../../../utils/helpers/themeHelpers';
import { useIgnoreKeyword } from '../../../../../../../../utils/hooks/useIgnoreKeyword';
import { useOrganizationSlug } from '../../../../../../../../utils/hooks/useOrganizationSlug';

import { getOptimizerRightBlockWrapper } from '../optimizerRightBlockHelpers';

const { Link } = Typography;

const classes = {
    blockLink: 'block-link',
    blockPopconfirm: 'block-popconfirm',
    blockPopconfirmDark: 'block-popconfirm-dark',
    link: 'link-hover-highlight',
    textDotted: 'text-decoration-dotted',
};

const KeywordBlockPopconfirm = ({ keywordItem }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const user = useSelector(selectUserData);
    const currentUserRoles = useSelector(selectCurrentUserRoles);
    const darkMode = useSelector(selectDarkMode);

    const organizationSlug = useOrganizationSlug();
    const ignoreKeyword = useIgnoreKeyword();

    const [isLoading, setIsLoading] = useState(false);

    const keyword = keywordItem?.keyword;
    const isUserMember = currentUserRoles?.some(
        (userRole) => roleTypes.organizationMember.key === userRole?.name
    );
    const filteredTerms = user?.organization?.settings?.[FILTERED_TERMS];
    const isBlocked = isKeywordBlocked(filteredTerms, keyword);

    const handleClick = () => {
        navigate(`/${organizationSlug}/account/settings`);
        batch(() => {
            dispatch(updateAccountTab(accountTabs['settings']?.key));
            dispatch(updateScrollSection(ScrollIds.advancedSettings));
        });
    };

    const popconfirmTitle = !isBlocked ? (
        <>
            Ignore this keyword and{' '}
            <Tooltip
                placement='top'
                title={
                    <>
                        Adds the keyword to your filter list under{' '}
                        <Link onClick={handleClick}>Advanced Settings</Link>.
                    </>
                }
                overlayClassName={getTooltipOverlayClass(darkMode)}
            >
                <span className={classes.textDotted}>block it</span>
            </Tooltip>{' '}
            from appearing in the future Optimizers?
        </>
    ) : (
        'Restore this keyword and allow it to appear in future Optimizers?'
    );

    const handleBlockKeyword = () => {
        setIsLoading(true);

        const newSetting = !isBlocked
            ? {
                  settings: {
                      [FILTERED_TERMS]: [...filteredTerms, { term: keyword }],
                  },
              }
            : {
                  settings: {
                      [FILTERED_TERMS]: [
                          ...filteredTerms?.filter(
                              (x) =>
                                  x?.term &&
                                  x.term?.toLocaleLowerCase() !== keyword?.toLocaleLowerCase()
                          ),
                      ],
                  },
              };

        axiosAPI
            .patch(`${organizationPathes.updateOrganization}/${user.organization.id}`, newSetting, {
                ...getAxiosHeaders(),
            })
            .then((res) => {
                if (res?.data?.settings) {
                    dispatch(
                        setUserData({
                            ...user,
                            organization: { ...user.organization, settings: res.data.settings },
                        })
                    );

                    if (!isCustomKeywordSource(keywordItem?.source) && keyword) {
                        ignoreKeyword(keywordItem, {
                            isBlocked: isKeywordBlocked(res.data.settings[FILTERED_TERMS], keyword),
                        });
                    }

                    openNotification(
                        notificationType.success,
                        'Success',
                        !isBlocked
                            ? 'Keyword has been successfully blocked'
                            : 'Keyword has been successfully unblocked'
                    );
                } else {
                    openBasicErrorNotification();
                }
            })
            .catch(() => openBasicErrorNotification())
            .finally(() => setIsLoading(false));
    };

    return (
        !isUserMember && (
            <Popconfirm
                placement='topRight'
                title={popconfirmTitle}
                onConfirm={handleBlockKeyword}
                okText='Yes'
                cancelText='Cancel'
                overlayClassName={`${classes.blockPopconfirm} ${
                    darkMode ? classes.blockPopconfirmDark : ''
                }`}
                getPopupContainer={getOptimizerRightBlockWrapper}
            >
                <Link
                    className={`${classes.link} ${classes.blockLink} ${getThemeColorClass(
                        darkMode
                    )}`}
                    underline
                    disabled={isLoading}
                >
                    {isBlocked ? 'Unblock' : 'Block'}
                </Link>
            </Popconfirm>
        )
    );
};

export default KeywordBlockPopconfirm;
